import { useCallback, useEffect, useState } from "react";

import { api } from "../lib/api";
import type { ProductCatalog } from "../lib/productCatalog";

let cachedCatalog: ProductCatalog | null = null;
let inFlightRequest: Promise<ProductCatalog> | null = null;

function loadProductCatalog(): Promise<ProductCatalog> {
  if (cachedCatalog) return Promise.resolve(cachedCatalog);
  if (inFlightRequest) return inFlightRequest;

  const request = api.getProductCatalog()
    .then((catalog) => {
      cachedCatalog = catalog;
      return catalog;
    })
    .finally(() => {
      if (inFlightRequest === request) inFlightRequest = null;
    });
  inFlightRequest = request;
  return request;
}

export function resetProductCatalogCacheForTests(): void {
  cachedCatalog = null;
  inFlightRequest = null;
}

export function useProductCatalog(): {
  catalog: ProductCatalog | null;
  loading: boolean;
  error: unknown;
  retry: () => void;
} {
  const [catalog, setCatalog] = useState<ProductCatalog | null>(cachedCatalog);
  const [loading, setLoading] = useState(cachedCatalog === null);
  const [error, setError] = useState<unknown>(null);
  const [requestVersion, setRequestVersion] = useState(0);

  const retry = useCallback(() => {
    cachedCatalog = null;
    setCatalog(null);
    setError(null);
    setLoading(true);
    setRequestVersion((version) => version + 1);
  }, []);

  useEffect(() => {
    let active = true;
    setLoading(cachedCatalog === null);
    loadProductCatalog()
      .then((nextCatalog) => {
        if (!active) return;
        setCatalog(nextCatalog);
        setError(null);
      })
      .catch((errorValue) => {
        if (!active) return;
        setCatalog(null);
        setError(errorValue);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [requestVersion]);

  return { catalog, loading, error, retry };
}
